"use client"

import {
  type ComponentPropsWithoutRef,
  type ComponentRef,
  type HTMLAttributes,
  type Ref,
} from "react"
import { AlertDialog as AlertDialogPrimitive } from "@base-ui/react/alert-dialog"
import { cn } from "cn"

import { ButtonProps, buttonVariants } from "@/components/ui/button"

const AlertDialog = AlertDialogPrimitive.Root

const AlertDialogTrigger = AlertDialogPrimitive.Trigger

const AlertDialogClose = AlertDialogPrimitive.Close

function AlertDialogContent({
  ref,
  className,
  children,
  ...props
}: ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Popup> & {
  ref?: Ref<ComponentRef<typeof AlertDialogPrimitive.Popup>>
}) {
  return (
    <AlertDialogPrimitive.Portal>
      <AlertDialogPrimitive.Backdrop className="fixed inset-0 z-overlay bg-black-a-7 duration-drawer data-ending-style:animate-out data-ending-style:fade-out data-open:animate-in data-open:fade-in sm:duration-dialog dark:bg-black-a-9 dark:backdrop-blur-2xs" />
      <AlertDialogPrimitive.Popup
        ref={ref}
        className="pointer-events-none! fixed inset-0 z-overlay flex animate-in items-end justify-center data-ending-style:animate-out sm:items-center"
        {...props}
      >
        <div
          className={cn(
            "pointer-events-auto relative flex w-full flex-col overflow-hidden rounded-t-2xl bg-background p-(--dialog-pad) shadow-ring-xl outline-hidden transition-[opacity,transform] duration-drawer ease-drawer in-data-closed:animate-out in-data-closed:fade-out in-data-closed:slide-out-to-bottom in-data-open:animate-in in-data-open:fade-in in-data-open:slide-in-from-bottom sm:max-w-md sm:rounded-2xl sm:duration-dialog sm:ease-in-out sm:in-data-closed:slide-out-to-top-4 sm:in-data-open:slide-in-from-top-4",
            className
          )}
        >
          {children}
        </div>
      </AlertDialogPrimitive.Popup>
    </AlertDialogPrimitive.Portal>
  )
}
AlertDialogContent.displayName = AlertDialogPrimitive.Popup.displayName

const AlertDialogHeader = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("pb-6 text-center", className)} {...props} />
)
AlertDialogHeader.displayName = "AlertDialogHeader"

const AlertDialogFooter = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse gap-2 pt-6 sm:flex-row sm:justify-end",
      className
    )}
    {...props}
  />
)
AlertDialogFooter.displayName = "AlertDialogFooter"

function AlertDialogTitle({
  ref,
  className,
  ...props
}: ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Title> & {
  ref?: Ref<HTMLHeadingElement>
}) {
  return (
    <AlertDialogPrimitive.Title
      ref={ref}
      className={cn(
        "text-lg leading-snug font-semibold text-pretty",
        className
      )}
      {...props}
    />
  )
}
AlertDialogTitle.displayName = AlertDialogPrimitive.Title.displayName

const AlertDialogDescription = AlertDialogPrimitive.Description

function AlertDialogAction({
  ref,
  className,
  variant = "destructive",
  ...props
}: ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Close> & {
  variant?: ButtonProps["variant"]
  ref?: Ref<ComponentRef<typeof AlertDialogPrimitive.Close>>
}) {
  return (
    <AlertDialogPrimitive.Close
      ref={ref}
      className={cn(
        buttonVariants({ variant, size: "md", block: true }),
        className
      )}
      {...props}
    />
  )
}
AlertDialogAction.displayName = AlertDialogPrimitive.Close.displayName

function AlertDialogCancel({
  ref,
  className,
  ...props
}: ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Close> & {
  ref?: Ref<ComponentRef<typeof AlertDialogPrimitive.Close>>
}) {
  return (
    <AlertDialogPrimitive.Close
      ref={ref}
      className={cn(
        buttonVariants({ variant: "outline", size: "md", block: true }),
        className
      )}
      {...props}
    />
  )
}
AlertDialogCancel.displayName = AlertDialogPrimitive.Close.displayName

export {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogClose,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
}
